import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { cartPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCartPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getCartPage } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CartPageProps = BasePageProps & {
  page: SanityCartPage
  site: SanitySiteFragment
}

const CartPage = ({ draftMode, locale, page, site }: CartPageProps) => {
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={cartPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<CartPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const cartPage = await getCartPage(sanityClient, context.locale)

  if (!cartPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(cartPage.page),
      site: getNonNullValues(cartPage.site),
    },
    revalidate: 60,
  }
}

export default CartPage
